import { addCalendarDays, calgaryDateString } from '../../../../utils/calgaryTime';
import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import './InvoiceWizard.css';
import { getRuntimeConfig, useRuntimeConfigStatus } from '@frontend/__settings__/runtimeConfig';
import InvoiceService from '../../../../AppSettings/services/InvoiceService';
import ClientService from '../../../../AppSettings/services/ClientService';
import MatterService from '../../../../AppSettings/services/MatterService';
import FinancialService from '../../../../AppSettings/services/FinancialService';
import ClientMattersService from '../../../../AppSettings/services/ClientMattersService';
import VenExpService from '../../../../AppSettings/services/VenExpService';
import OppExpService from '../../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../../AppSettings/services/GenExpensesService';
import generateInvoicePDF from '../InvoiceGen/InvoicePDFGen';
import normalizeToArray from '../../../../helpers/responseHelpers';
import type { UninvoicedInvoiceScope } from '../../../../AppSettings/services/InvoiceService';
import { CATEGORIES, matchesInvoiceScope } from '../UninvoicedPanel/workspaceModel';
import { computeBilledTimeAmount, parseTimeToHours } from '../../../../helpers/billingHelpers';
import {
  consolidateInvoiceLineItems,
  isGroupableInvoiceLineItem,
} from '../../../../helpers/invoiceLineItems';

function getExpenseRowId(item: any): string {
  return item.disbursementid || item.ven_id || item.opp_id || item.gen_id || item.ge_id || '';
}

/** Collect source-row ids from saved invoice line_items, keyed by sourceType. */
function linkedSourceIdsByType(lineItems: any[] | null | undefined): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const li of lineItems || []) {
    const sourceType = String(li?.sourceType || '').trim();
    if (!sourceType || li?.id == null || li.id === '') continue;
    let set = map.get(sourceType);
    if (!set) {
      set = new Set<string>();
      map.set(sourceType, set);
    }
    set.add(String(li.id));
  }
  return map;
}

function isLinkedExpense(exp: any, linkedIds: Map<string, Set<string>>): boolean {
  const id = String(getExpenseRowId(exp));
  if (!id) return false;
  if (exp.ge_id != null || exp.gen_id != null) {
    return Boolean(
      linkedIds.get('gen expense')?.has(id) || linkedIds.get('general expense')?.has(id)
    );
  }
  if (exp.opp_id != null) return Boolean(linkedIds.get('opp expense')?.has(id));
  if (exp.ven_id != null) return Boolean(linkedIds.get('ven expense')?.has(id));
  return Boolean(linkedIds.get('disbursement')?.has(id));
}

function dedupeByKey<T>(items: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const key = keyFn(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function pickPublicationNo(matter: Record<string, unknown> | null | undefined): string {
  if (!matter || typeof matter !== 'object') return '';
  const raw =
    matter.publication_no ??
    (matter as { matter_publication_no?: string | null }).matter_publication_no;
  if (raw == null) return '';
  return String(raw).trim();
}

/** LSD in UI ↔ `matters.registration_no` (and snapshot on `invoices.registration_no`). */
function pickRegistrationNo(matter: Record<string, unknown> | null | undefined): string {
  if (!matter || typeof matter !== 'object') return '';
  const raw =
    matter.registration_no ??
    (matter as { matter_registration_no?: string | null }).matter_registration_no;
  if (raw == null) return '';
  return String(raw).trim();
}

const INT32_MAX = 2_147_483_647;

function maxInvoiceIdFromList(invoices: unknown[]): number {
  let m = 0;
  for (const inv of invoices) {
    if (!inv || typeof inv !== 'object') continue;
    const rec = inv as { invoice_number?: unknown; invoiceid?: unknown };
    const id = rec.invoice_number ?? rec.invoiceid;
    const n = typeof id === 'number' ? id : parseInt(String(id ?? ''), 10);
    if (!Number.isNaN(n) && n > m) m = n;
  }
  return m;
}

function nextInvoiceIdStringFromList(invoices: unknown[]): string {
  const next = maxInvoiceIdFromList(invoices) + 1;
  return String(Math.min(next, INT32_MAX));
}

function sanitizeSerial4Input(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  let n = parseInt(digits, 10);
  if (Number.isNaN(n)) return '';
  n = Math.min(n, INT32_MAX);
  n = Math.max(n, 0);
  return String(n);
}

function displayInvoiceIdFromRow(row: { invoice_number?: unknown; invoiceid?: unknown } | null | undefined): string {
  if (!row) return '';
  const v = row.invoice_number ?? row.invoiceid;
  if (v == null) return '';
  return String(v).trim();
}

export interface InvoiceWizardData {
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  clientCompany: string;
  matterId: string;
  matterName: string;
  invoiceNumber: string;
  issueDate: string;
  selectedBillableTimes: any[];
  selectedFixedFees: any[];
  selectedExpenses: any[];
  lineItems: any[];
  dueDate: string;
  poNumber: string;
  notesToClient: string;
  additionalRecipients: string;
  enableAutoReminders: boolean;
  discount: number;
  taxRate: number;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
}

interface LineItem {
  type: string;
  description: string;
  item: string;
  qty: number;
  amount: number;
  discount: number;
  id?: string;
  sourceType?: string;
}

/** Manual rows keep qty/amount as raw strings so inputs can be empty while typing */
interface ManualLineItemDraft {
  type: string;
  description: string;
  item: string;
  qty: string;
  amount: string;
  discount: string;
  // Link keys for rows imported from source tables (billed time, fixed fees,
  // expenses, other billing). Undefined for genuine free-text manual rows.
  id?: string;
  sourceType?: string;
}

/** Allow only digits and a single decimal point */
function sanitizeDecimalInput(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}

interface InvoiceWizardProps {
  onClose: () => void;
  existingInvoice?: any;
  initialClientId?: string;
  initialMatterId?: string;
  initialScope?: UninvoicedInvoiceScope;
  readOnly?: boolean;
}

const InvoiceWizard: React.FC<InvoiceWizardProps> = ({ onClose, existingInvoice, initialClientId, initialMatterId, initialScope, readOnly = false }) => {
  const [workspaceScope, setWorkspaceScope] = useState(initialScope);
  const { data: clients = [] } = useQuery({
    queryKey: ['Clients', 'orbit'],
    queryFn: () => ClientService.getClients(),
  });
  const initialClientAppliedRef = useRef(false);
  /** After any manual edit, we keep the field as-is and do not overwrite with a new auto-suggested id. */
  const userEditedInvoiceNumber = useRef(false);
  /** Once the user edits Tax, stop auto-syncing it to the GST default. */
  const userEditedTaxRate = useRef(false);
  const invoiceNumberHintId = useId();

  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invoiceFrom, setInvoiceFrom] = useState<any>(null);
  const [showImportPicker, setShowImportPicker] = useState(false);

  // Sender defaults come from the tenant's runtime config (P1) when ANY sender field is
  // set — atomically, so a partially-filled row yields blanks rather than a letterhead
  // mixing this tenant's name with NCAL's address. The NCAL letterhead (incl. GST) stays
  // as the all-or-nothing fallback so unregistered deployments are unchanged.
  const sender = getRuntimeConfig()?.invoiceSender;
  const senderConfigured = Boolean(
    sender && (sender.name || sender.address || sender.email || sender.phone || sender.gstNumber),
  );
  const [fromName, setFromName] = useState(senderConfigured ? sender?.name ?? '' : 'Nielsen, Clark & Associates Ltd.\no/a NCAL Engineering');
  const [fromAddress, setFromAddress] = useState(senderConfigured ? sender?.address ?? '' : '3628 Burnsland Rd SE\nCalgary, AB, T2G 3Z2');
  const [fromPhone, setFromPhone] = useState(senderConfigured ? sender?.phone ?? '' : '(403) 460-5000');

  const [clientInfo, setClientInfo] = useState({
    id: '', name: '', email: '', phone: '', address: '', company: '',
  });
  const [matterName, setMatterName] = useState('');
  const [afeNumber, setAfeNumber] = useState('');
  const [lsdNumber, setLsdNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [matterId, setMatterId] = useState('');
  const [issueDate, setIssueDate] = useState(calgaryDateString());
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    if (existingInvoice) {
      const d = displayInvoiceIdFromRow(existingInvoice);
      if (d) return d;
    }
    return '';
  });
  const [dueDate, setDueDate] = useState(() => {
    return addCalendarDays(calgaryDateString(), 30);
  });
  // Kept as strings so the fields can be truly empty (no default 0 in the way)
  const [discount, setDiscount] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const discountNum = parseFloat(discount) || 0;
  const taxRateNum = parseFloat(taxRate) || 0;
  const amountPaidNum = parseFloat(amountPaid) || 0;
  const [notesToClient, setNotesToClient] = useState(
    'Payment is due within 30 days. Thank you for your continued business.'
  );
  const [addDataDump, setAddDataDump] = useState(true);
  const [blackAndWhitePdf, setBlackAndWhitePdf] = useState(false);
  const [billingPeriodStart, setBillingPeriodStart] = useState(initialScope?.start_date || '');
  const [billingPeriodEnd, setBillingPeriodEnd] = useState(initialScope?.end_date || '');
  const [terms, setTerms] = useState('');
  const [gstNumber, setGstNumber] = useState(senderConfigured ? sender?.gstNumber ?? '' : '811818848RT0001');
  const [fromEmail, setFromEmail] = useState(senderConfigured ? sender?.email ?? '' : 'invoices@ncaleng.ca');

  const [manualLineItems, setManualLineItems] = useState<ManualLineItemDraft[]>([]);

  // Client/matter selection
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Client matters
  const [clientMatters, setClientMatters] = useState<any[]>([]);
  const [loadingFinancials, setLoadingFinancials] = useState(Boolean(existingInvoice));

  // Unbilled items
  const [billableTimes, setBillableTimes] = useState<any[]>([]);
  const [fixedFees, setFixedFees] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [otherBilling, setOtherBilling] = useState<any[]>([]);
  // Rows already linked to the invoice being edited — used for PDF data dump on
  // Update & Download (they are filtered out of the unbilled import lists).
  const [linkedDumpSources, setLinkedDumpSources] = useState<{
    billableTimes: any[];
    fixedFees: any[];
    expenses: any[];
    otherBilling: any[];
  }>({ billableTimes: [], fixedFees: [], expenses: [], otherBilling: [] });
  const [selectedBTIds, setSelectedBTIds] = useState<Set<string>>(new Set());
  const [selectedFFIds, setSelectedFFIds] = useState<Set<string>>(new Set());
  const [selectedExpIds, setSelectedExpIds] = useState<Set<string>>(new Set());
  const [selectedOBIds, setSelectedOBIds] = useState<Set<string>>(new Set());

  const fetchNextInvoiceIdString = useCallback(async (): Promise<string> => {
    const raw = await InvoiceService.getAllInvoices().catch(() => []);
    const list = normalizeToArray(raw);
    return nextInvoiceIdStringFromList(list);
  }, []);

  /** `getClientMatters` rows often omit AFE/LSD; matter details matches the matter-details invoice wizard. */
  const matterFieldsHydrateSeq = useRef(0);
  const syncMatterFieldsFromDetails = useCallback(async (mid: string | null | undefined) => {
    if (!mid || !String(mid).trim()) {
      setAfeNumber('');
      setLsdNumber('');
      return;
    }
    const seq = ++matterFieldsHydrateSeq.current;
    try {
      const m = await MatterService.getMatterDetails(mid);
      if (seq !== matterFieldsHydrateSeq.current) return;
      if (!m) return;
      const rec = m as unknown as Record<string, unknown>;
      setAfeNumber(pickPublicationNo(rec));
      setLsdNumber(pickRegistrationNo(rec));
    } catch {
      if (seq !== matterFieldsHydrateSeq.current) return;
    }
  }, []);

  useEffect(() => {
    if (existingInvoice) return;
    if (userEditedInvoiceNumber.current) return;
    if (invoiceNumber !== '') return;
    let cancelled = false;
    fetchNextInvoiceIdString().then((next) => {
      if (cancelled) return;
      if (userEditedInvoiceNumber.current) return;
      setInvoiceNumber(next);
    });
    return () => { cancelled = true; };
  }, [existingInvoice, invoiceNumber, fetchNextInvoiceIdString]);

  /** GST is a flat 5%, so new invoices default to a 5% tax. Existing invoices keep their saved rate, and any manual edit stops the auto-default. */
  useEffect(() => {
    if (existingInvoice) return;
    if (userEditedTaxRate.current) return;
    setTaxRate('5');
  }, [existingInvoice]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      try {
        const invoiceFromData = await InvoiceService.getInvoiceFrom().catch(() => []);
        if (cancelled) return;

        if (Array.isArray(invoiceFromData) && invoiceFromData.length > 0) {
          const f = invoiceFromData[0] as any;
          setInvoiceFrom(f);
          if (f.gst_number) setGstNumber(f.gst_number);
        }

        if (existingInvoice) {
          await loadExistingInvoice(cancelled);
        }
      } catch (err) {
        console.error('Error initializing invoice creator:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Same client list cache as SEPT Orbit — apply URL/prefill once clients load */
  useEffect(() => {
    if (existingInvoice || initialClientAppliedRef.current) return;
    if (!initialClientId || !Array.isArray(clients) || clients.length === 0) return;
    const matchedClient = clients.find(
      (c: any) => (c.ClientID || c.client_id) === initialClientId
    );
    if (!matchedClient) return;
    initialClientAppliedRef.current = true;
    setClientInfo({
      id: matchedClient.ClientID || matchedClient.client_id || '',
      name: `${matchedClient.FirstName || matchedClient.first_name || ''} ${matchedClient.LastName || matchedClient.last_name || ''}`.trim() || 'Unnamed Client',
      email: matchedClient.Email || matchedClient.email || '',
      phone: matchedClient.Phone || matchedClient.phone || '',
      address: matchedClient.Address || matchedClient.address || '',
      company: matchedClient.Company || matchedClient.company || '',
    });
    if (initialMatterId) {
      setMatterId(initialMatterId);
    }
  }, [clients, initialClientId, initialMatterId, existingInvoice]);

  const loadExistingInvoice = async (cancelled: boolean) => {
    if (!existingInvoice) return;
    // Unconditional, and before any await: an invoice saved without notes (NULL) opens empty,
    // not with the new-invoice default.
    setNotesToClient(existingInvoice.notes_to_client ?? '');

    const d = displayInvoiceIdFromRow(existingInvoice);
    if (d) {
      setInvoiceNumber(d);
    } else {
      const raw = await InvoiceService.getAllInvoices().catch(() => []);
      if (cancelled) return;
      setInvoiceNumber(nextInvoiceIdStringFromList(normalizeToArray(raw)));
    }
    setIssueDate(existingInvoice.created_at?.split('T')[0] || calgaryDateString());
    setDueDate(existingInvoice.due_date || dueDate);
    const storedDiscount = parseFloat(existingInvoice.discount) || 0;
    const storedTaxRate = parseFloat(existingInvoice.tax_rate) || 0;
    const storedAmountPaid = parseFloat(existingInvoice.amount_paid) || 0;
    setDiscount(storedDiscount ? String(storedDiscount) : '');
    setTaxRate(storedTaxRate ? String(storedTaxRate) : '');
    setAmountPaid(storedAmountPaid ? String(storedAmountPaid) : '');
    setMatterId(existingInvoice.matterid || '');

    if (existingInvoice.line_items) {
      setManualLineItems(existingInvoice.line_items.map((li: any) => {
        const qty = parseFloat(li.qty) || 1;
        const amount = parseFloat(li.amount) || 0;
        const disc = parseFloat(li.discount) || 0;
        return {
          type: li.type || 'money_in',
          description: li.description || '',
          item: li.item || '',
          qty: String(qty),
          amount: amount ? String(amount) : '',
          discount: disc ? String(disc) : '',
          // Preserve link keys so update_invoice can re-stamp invoiceid on the
          // source rows rather than orphaning them (blank Invoice # in exports).
          id: li.id,
          sourceType: li.sourceType,
        };
      }));
    }
    if (existingInvoice.billing_period_start) setBillingPeriodStart(existingInvoice.billing_period_start);
    if (existingInvoice.billing_period_end) setBillingPeriodEnd(existingInvoice.billing_period_end);
    if (existingInvoice.terms) setTerms(existingInvoice.terms);
    if (existingInvoice.gst_number) setGstNumber(existingInvoice.gst_number);
    if (existingInvoice.po_number != null) setPoNumber(String(existingInvoice.po_number));

    let willFetchFinancials = false;
    if (existingInvoice.client && existingInvoice.clientid) {
      setClientInfo({
        id: existingInvoice.clientid || '',
        name: existingInvoice.client.name || '',
        email: existingInvoice.client.email || '',
        phone: existingInvoice.client.phone || '',
        address: existingInvoice.client.address || '',
        company: existingInvoice.client.company || '',
      });
      willFetchFinancials = Boolean(existingInvoice.clientid);
    } else if (existingInvoice.clientid) {
      try {
        const c = await ClientService.getClientById(existingInvoice.clientid);
        if (cancelled) return;
        if (c) {
          setClientInfo({
            id: c.ClientID || c.client_id || '',
            name: `${c.FirstName || c.first_name || ''} ${c.LastName || c.last_name || ''}`.trim() || 'Unnamed Client',
            email: c.Email || c.email || '',
            phone: c.Phone || c.phone || '',
            address: c.Address || c.address || '',
            company: c.Company || c.company || '',
          });
          willFetchFinancials = true;
        }
      } catch { /* continue without client */ }
    }

    if (existingInvoice.matterid) {
      try {
        const m = await MatterService.getMatterDetails(existingInvoice.matterid);
        if (cancelled) return;
        if (m) {
          setMatterName(m.mattername || m.matterid || '');
          const rec = m as unknown as Record<string, unknown>;
          const afeFromMatter = pickPublicationNo(rec);
          const lsdFromMatter = pickRegistrationNo(rec);
          const storedAfe = existingInvoice.publication_no;
          const storedLsd = existingInvoice.registration_no;
          setAfeNumber(
            storedAfe != null && String(storedAfe).trim() !== ''
              ? String(storedAfe)
              : afeFromMatter
          );
          setLsdNumber(
            storedLsd != null && String(storedLsd).trim() !== ''
              ? String(storedLsd)
              : lsdFromMatter
          );
        }
      } catch { /* continue without matter */ }
    }

    // No client → financials fetch never runs; clear the initial loading gate.
    if (!willFetchFinancials) {
      setLoadingFinancials(false);
      setLinkedDumpSources({ billableTimes: [], fixedFees: [], expenses: [], otherBilling: [] });
    }
  };

  // Fetch all client financials whenever client changes
  useEffect(() => {
    if (!clientInfo.id) return;
    let cancelled = false;

    async function fetchClientFinancials() {
      setLoadingFinancials(true);
      try {
        // Fetch client matters, billable time, and fixed fees in parallel
        const [mattersData, btData, ffData] = await Promise.all([
          ClientMattersService.getClientMatters(clientInfo.id).catch(() => []),
          FinancialService.getBilledTimeByClientId(clientInfo.id).catch(() => []),
          FinancialService.getAssignedFixedFeesByClientId(clientInfo.id).catch(() => []),
        ]);
        if (cancelled) return;

        const matters = normalizeToArray(mattersData).filter(Boolean);
        setClientMatters(matters);

        // When editing, keep matter / AFE / LSD from loadExistingInvoice — do not
        // overwrite stored snapshots (multi-matter "All projects" used to clear them).
        if (!existingInvoice) {
          if (initialMatterId && matters.length > 0) {
            const match = matters.find((m: any) => m.matterid === initialMatterId);
            if (match) {
              setMatterId(match.matterid || '');
              setMatterName(match.mattername || match.matterid || '');
              await syncMatterFieldsFromDetails(match.matterid);
              if (cancelled) return;
            } else {
              setMatterId(initialMatterId);
              try {
                const md = await MatterService.getMatterDetails(initialMatterId);
                if (cancelled) return;
                if (md) {
                  setMatterName(md.mattername || initialMatterId);
                  const rec = md as unknown as Record<string, unknown>;
                  setAfeNumber(pickPublicationNo(rec));
                  setLsdNumber(pickRegistrationNo(rec));
                }
              } catch { /* AFE / LSD / name stay empty until user picks project */ }
            }
          } else if (!matterId && matters.length === 1) {
            setMatterId(matters[0].matterid || '');
            setMatterName(matters[0].mattername || matters[0].matterid || '');
            await syncMatterFieldsFromDetails(matters[0].matterid);
            if (cancelled) return;
          } else if (!initialMatterId && matters.length > 1) {
            setMatterName('All projects');
            setAfeNumber('');
            setLsdNumber('');
          }
        }

        const linkedIds = existingInvoice?.line_items
          ? linkedSourceIdsByType(existingInvoice.line_items)
          : null;

        const allBT = normalizeToArray(btData)
          .filter(Boolean)
          .filter((i: any) => i.is_billable !== false); // A3: exclude non-billable time from invoice import
        const filteredBT = allBT.filter((i: any) => !i.invoiceid);
        const allFF = normalizeToArray(ffData).filter(Boolean);
        const filteredFF = allFF.filter((i: any) => !i.invoiceid);

        // Load expenses / other billing for all client matters so changing Project can re-scope without refetch
        let allExp: any[] = [];
        let allOB: any[] = [];
        let linkedExp: any[] = [];
        let linkedOB: any[] = [];
        const matterIdsFromMatters = matters.map((m: any) => m.matterid).filter(Boolean);
        const matterIds = Array.from(
          new Set(
            [
              ...matterIdsFromMatters,
              initialMatterId,
              existingInvoice?.matterid,
            ].filter(Boolean).map(String)
          )
        );
        if (matterIds.length > 0) {
          try {
            const [venLists, oppLists, genLists, obLists, disbLists] = await Promise.all([
              Promise.all(matterIds.map((id: string) => VenExpService.getVenExpensesByMatter(id).catch(() => []))),
              Promise.all(matterIds.map((id: string) => OppExpService.getOppExpensesByMatter(id).catch(() => []))),
              Promise.all(matterIds.map((id: string) => GenExpensesService.getGenExpensesByMatter(id).catch(() => []))),
              Promise.all(matterIds.map((id: string) => FinancialService.getOtherBillingByMatter(id).catch(() => []))),
              Promise.all(matterIds.map((id: string) => FinancialService.getAgentDisbursements(id).catch(() => []))),
            ]);
            if (cancelled) return;
            const venFlat = venLists.flat();
            const oppFlat = oppLists.flat();
            const genFlat = genLists.flatMap((r: any) => normalizeToArray(r?.data ?? r));
            const obFlat = obLists.flatMap((r: any) => normalizeToArray(r));
            const disbFlat = disbLists.flatMap((r: any) => normalizeToArray(r));
            const isUnbilled = (exp: any) => {
              if (exp.ge_id != null || exp.gen_id != null) return !exp.invoiceid;
              if (exp.ven_id != null || exp.opp_id != null) return !exp.invoiced;
              return !exp.invoiceid;
            };
            const expRaw = [...venFlat, ...oppFlat, ...genFlat, ...disbFlat].filter(Boolean);
            allExp = expRaw.filter(isUnbilled);
            allOB = obFlat.filter(Boolean).filter((ob: any) => !ob.invoiceid);
            if (linkedIds) {
              linkedExp = expRaw.filter((exp) => isLinkedExpense(exp, linkedIds));
              linkedOB = obFlat
                .filter(Boolean)
                .filter((ob: any) => linkedIds.get('other billing')?.has(String(ob.ob_id)));
            }
          } catch { /* continue without expenses */ }
        }

        setBillableTimes(filteredBT);
        setFixedFees(filteredFF);
        setExpenses(allExp);
        setOtherBilling(allOB);
        setLinkedDumpSources({
          billableTimes: linkedIds
            ? allBT.filter((i: any) => linkedIds.get('billed time')?.has(String(i.bt_id)))
            : [],
          fixedFees: linkedIds
            ? allFF.filter((i: any) => linkedIds.get('fixed fee')?.has(String(i.assignedid)))
            : [],
          expenses: linkedExp,
          otherBilling: linkedOB,
        });
      } catch (err) {
        console.error('Error fetching client financials:', err);
      } finally {
        if (!cancelled) setLoadingFinancials(false);
      }
    }

    fetchClientFinancials();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo.id]);

  const matterMatches = useCallback((rowMatterId: unknown) => {
    if (!matterId) return true;
    return String(rowMatterId ?? '') === String(matterId);
  }, [matterId]);

  const matterScopedBillableTimes = useMemo(
    () => billableTimes.filter((i: any) => matterMatches(i.matterid) && matchesInvoiceScope(i, 'billed_time', workspaceScope)),
    [billableTimes, matterMatches, workspaceScope]
  );
  const matterScopedFixedFees = useMemo(
    () => fixedFees.filter((i: any) => matterMatches(i.matterid) && matchesInvoiceScope(i, 'fixed_fees', workspaceScope)),
    [fixedFees, matterMatches, workspaceScope]
  );
  const matterScopedExpenses = useMemo(
    () => expenses.filter((i: any) => matterMatches(i.matterid) && matchesInvoiceScope(i, i.disbursementid != null ? 'disbursements' : 'expenses', workspaceScope)),
    [expenses, matterMatches, workspaceScope]
  );
  const matterScopedOtherBilling = useMemo(
    () => otherBilling.filter((i: any) => matterMatches(i.matterid) && matchesInvoiceScope(i, 'other_billing', workspaceScope)),
    [otherBilling, matterMatches, workspaceScope]
  );

  /** When Project (matter) changes, re-sync import selections to that scope */
  useEffect(() => {
    if (!clientInfo.id) return;
    const hasFinancials =
      billableTimes.length > 0 ||
      fixedFees.length > 0 ||
      expenses.length > 0 ||
      otherBilling.length > 0;
    if (!hasFinancials) return;

    const sBT = matterScopedBillableTimes;
    const sFF = matterScopedFixedFees;
    const sExp = matterScopedExpenses;
    const sOB = matterScopedOtherBilling;

    if (existingInvoice?.invoiceid) {
      setSelectedBTIds((prev) =>
        new Set(Array.from(prev).filter((id) => sBT.some((i: any) => i.bt_id === id)))
      );
      setSelectedFFIds((prev) =>
        new Set(Array.from(prev).filter((id) => sFF.some((i: any) => i.assignedid === id)))
      );
      setSelectedExpIds((prev) =>
        new Set(Array.from(prev).filter((id) => sExp.some((exp) => getExpenseRowId(exp) === id)))
      );
      setSelectedOBIds((prev) =>
        new Set(Array.from(prev).filter((id) => sOB.some((i: any) => i.ob_id === id)))
      );
    } else {
      setSelectedBTIds(new Set(sBT.map((i: any) => i.bt_id)));
      setSelectedFFIds(new Set(sFF.map((i: any) => i.assignedid)));
      setSelectedExpIds(new Set(sExp.map((exp) => getExpenseRowId(exp))));
      setSelectedOBIds(new Set(sOB.map((i: any) => i.ob_id)));
    }
  }, [
    matterId,
    clientInfo.id,
    billableTimes.length,
    fixedFees.length,
    expenses.length,
    otherBilling.length,
    matterScopedBillableTimes,
    matterScopedFixedFees,
    matterScopedExpenses,
    matterScopedOtherBilling,
    existingInvoice?.invoiceid,
  ]);

  /** sourceType for invoice line_items so backend updates the correct table's invoiceid */
  const getExpSourceType = (item: any): string => {
    if (item.ge_id != null || item.gen_id != null) return 'gen expense';
    if (item.opp_id != null) return 'opp expense';
    if (item.ven_id != null) return 'ven expense';
    return 'disbursement';
  };

  const toggleBT = (id: string) => {
    setSelectedBTIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleFF = (id: string) => {
    setSelectedFFIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleExp = (id: string) => {
    setSelectedExpIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleOB = (id: string) => {
    setSelectedOBIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const isInBillingPeriod = useCallback((record: any): boolean => {
    if (!billingPeriodStart && !billingPeriodEnd) return true;
    const dateStr = record.date_recorded || record.date_added || record.date || record.expense_date || record.created_at || '';
    if (!dateStr) return true;
    const d = dateStr.split('T')[0];
    return (!billingPeriodStart || d >= billingPeriodStart) && (!billingPeriodEnd || d <= billingPeriodEnd);
  }, [billingPeriodStart, billingPeriodEnd]);

  const importedLineItems = useMemo(() => {
    const items: LineItem[] = [];
    matterScopedBillableTimes
      .filter(bt => selectedBTIds.has(bt.bt_id) && isInBillingPeriod(bt) && parseTimeToHours(bt.time_recorded) > 0)
      .forEach(bt => {
        const hours = parseTimeToHours(bt.time_recorded);
        const total = computeBilledTimeAmount(bt);
        items.push({
          type: 'money_in',
          description: bt.employee_name || 'Unknown',
          item: bt.notes || '',
          qty: hours,
          // Effective per-hour rate so qty × amount reproduces the true total
          amount: total / hours,
          discount: 0,
          id: bt.bt_id,
          sourceType: 'billed time',
        });
      });
    matterScopedFixedFees.filter(ff => selectedFFIds.has(ff.assignedid) && isInBillingPeriod(ff)).forEach(ff => {
      const narrative = String(ff.narrative || '').trim();
      const feeCode = String(ff.fee_code || '').trim();
      items.push({
        type: 'money_in',
        description: narrative || 'Fixed fee',
        item: feeCode,
        qty: 1,
        amount: parseFloat(ff.fees) || 0,
        discount: 0,
        id: ff.assignedid,
        sourceType: 'fixed fee',
      });
    });
    matterScopedExpenses.filter(exp => selectedExpIds.has(getExpenseRowId(exp)) && isInBillingPeriod(exp)).forEach(exp => {
      items.push({
        type: 'money_out',
        description: exp.description || 'Disbursements',
        item: 'Disbursements',
        qty: typeof exp.quantity === 'number' ? exp.quantity : 1,
        amount: typeof exp.amount === 'number' ? exp.amount : parseFloat(exp.amount || exp.fees || '0') || 0,
        discount: 0,
        id: getExpenseRowId(exp),
        sourceType: getExpSourceType(exp),
      });
    });
    matterScopedOtherBilling.filter(ob => selectedOBIds.has(ob.ob_id) && isInBillingPeriod(ob)).forEach(ob => {
      items.push({
        type: 'money_in',
        description: ob.description || 'Other Billing',
        item: '',
        qty: 1,
        amount: parseFloat(ob.amount) || 0,
        discount: 0,
        id: ob.ob_id,
        sourceType: 'other billing',
      });
    });
    return items;
  }, [
    matterScopedBillableTimes,
    matterScopedFixedFees,
    matterScopedExpenses,
    matterScopedOtherBilling,
    selectedBTIds,
    selectedFFIds,
    selectedExpIds,
    selectedOBIds,
    isInBillingPeriod,
  ]);

  const parsedManualLineItems = useMemo<LineItem[]>(() => {
    return manualLineItems.map(m => ({
      type: m.type,
      id: m.id,
      sourceType: m.sourceType,
      description: m.description,
      item: m.item,
      qty: parseFloat(m.qty) || 1,
      amount: parseFloat(m.amount) || 0,
      discount: parseFloat(m.discount) || 0,
    }));
  }, [manualLineItems]);

  const allLineItems = useMemo(() => {
    return [...importedLineItems, ...parsedManualLineItems];
  }, [importedLineItems, parsedManualLineItems]);

  // Groupable saved rows (billed time / fixed fee with a cost code) are shown
  // consolidated like create-mode imports. Non-groupable manuals stay editable.
  const editableManualIndices = useMemo(() => {
    return parsedManualLineItems
      .map((li, i) => (isGroupableInvoiceLineItem(li) ? -1 : i))
      .filter((i) => i >= 0);
  }, [parsedManualLineItems]);

  const displayImportedItems = useMemo(() => {
    const groupableSaved = parsedManualLineItems.filter(isGroupableInvoiceLineItem);
    return consolidateInvoiceLineItems([
      ...importedLineItems,
      ...groupableSaved,
    ]) as LineItem[];
  }, [importedLineItems, parsedManualLineItems]);

  const displayAllLineItems = useMemo(() => {
    const editableManuals = editableManualIndices.map((i) => parsedManualLineItems[i]);
    return [...displayImportedItems, ...editableManuals];
  }, [displayImportedItems, editableManualIndices, parsedManualLineItems]);

  const totals = useMemo(() => {
    let subtotal = 0;
    allLineItems.forEach(item => {
      // A20: foot the per-line DISPLAYED amount — each Amount cell shows fmt(amount×qty) (cent-rounded),
      // so round to cents here too; otherwise sub-cent rates make the printed lines not add up to the total.
      const amt = Math.round((item.amount || 0) * (item.qty || 1) * 100) / 100;
      const disc = amt * ((item.discount || 0) / 100);
      subtotal += amt - disc;
    });
    const discountAmt = subtotal * (discountNum / 100);
    const afterDiscount = subtotal - discountAmt;
    const tax = afterDiscount * (taxRateNum / 100);
    const total = afterDiscount + tax;
    const balanceDue = total - amountPaidNum;
    return { subtotal, discountAmt, afterDiscount, tax, total, balanceDue };
  }, [allLineItems, discountNum, taxRateNum, amountPaidNum]);

  const addManualLineItem = () => {
    setManualLineItems(prev => [...prev, { type: 'money_in', description: '', item: '', qty: '1', amount: '', discount: '' }]);
  };

  const updateManualLineItem = (index: number, field: keyof ManualLineItemDraft, value: string) => {
    setManualLineItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeManualLineItem = (index: number) => {
    setManualLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const totalItemCount =
    matterScopedBillableTimes.length +
    matterScopedFixedFees.length +
    matterScopedExpenses.length +
    matterScopedOtherBilling.length;
  const selectedImportCount = useMemo(() => {
    let n = 0;
    matterScopedBillableTimes.forEach((i: any) => {
      if (selectedBTIds.has(i.bt_id)) n += 1;
    });
    matterScopedFixedFees.forEach((i: any) => {
      if (selectedFFIds.has(i.assignedid)) n += 1;
    });
    matterScopedExpenses.forEach((exp) => {
      if (selectedExpIds.has(getExpenseRowId(exp))) n += 1;
    });
    matterScopedOtherBilling.forEach((i: any) => {
      if (selectedOBIds.has(i.ob_id)) n += 1;
    });
    return n;
  }, [
    matterScopedBillableTimes,
    matterScopedFixedFees,
    matterScopedExpenses,
    matterScopedOtherBilling,
    selectedBTIds,
    selectedFFIds,
    selectedExpIds,
    selectedOBIds,
  ]);

  const handleClientSelect = (client: any) => {
    if (workspaceScope && (client.ClientID || client.client_id) !== initialClientId) {
      setWorkspaceScope(undefined);
    }
    setClientInfo({
      id: client.ClientID || client.client_id || '',
      name: `${client.FirstName || client.first_name || ''} ${client.LastName || client.last_name || ''}`.trim() || 'Unnamed Client',
      email: client.Email || client.email || '',
      phone: client.Phone || client.phone || '',
      address: client.Address || client.address || '',
      company: client.Company || client.company || '',
    });
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter((c) => {
      const name = `${c.FirstName || c.first_name || ''} ${c.LastName || c.last_name || ''}`.toLowerCase();
      const company = (c.Company || c.company || '').toLowerCase();
      const id = String(c.ClientID || c.client_id || '').toLowerCase();
      return name.includes(q) || company.includes(q) || id.includes(q);
    });
  }, [clients, clientSearch]);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      let refNo = invoiceNumber.trim();
      if (!refNo) {
        refNo = await fetchNextInvoiceIdString();
      }
      const pub = afeNumber.trim();
      const lsd = lsdNumber.trim();
      const po = poNumber.trim();
      const invoiceData: any = {
        matterid: matterId || null,
        clientid: clientInfo.id,
        due_date: dueDate || null,
        discount: discountNum,
        tax_rate: taxRateNum,
        total_amount: totals.total,
        line_items: allLineItems,
        acc_rec_total: totals.subtotal,
        acc_pay_total: 0,
        amount_paid: String(amountPaidNum),
        publication_no: pub || null,
        registration_no: lsd || null,
        po_number: po || null,
        billing_period_start: billingPeriodStart || null,
        billing_period_end: billingPeriodEnd || null,
        // '' (not null) when cleared: the PUT drops null keys, and the server stores '' as NULL.
        notes_to_client: notesToClient.trim(),
      };

      if (existingInvoice?.invoiceid) {
        invoiceData.invoiceid = existingInvoice.invoiceid;
        await InvoiceService.updateInvoice(invoiceData);
      } else {
        const rid = parseInt(refNo, 10);
        if (!Number.isNaN(rid) && rid >= 1 && rid <= INT32_MAX) {
          invoiceData.requested_invoice_number = rid;
        }
        await InvoiceService.createInvoice(invoiceData);
      }

      const formatPeriodDate = (d: string) => {
        if (!d) return '';
        const parts = d.split('-');
        if (parts.length !== 3) return d;
        const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return `${months[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
      };
      const periodStr = billingPeriodStart && billingPeriodEnd
        ? `${formatPeriodDate(billingPeriodStart)} - ${formatPeriodDate(billingPeriodEnd)}`
        : '';

      try {
        const selectedDumpBT = matterScopedBillableTimes
          .filter(bt => selectedBTIds.has(bt.bt_id) && isInBillingPeriod(bt) && parseTimeToHours(bt.time_recorded) > 0);
        const selectedDumpFF = matterScopedFixedFees.filter(
          ff => selectedFFIds.has(ff.assignedid) && isInBillingPeriod(ff)
        );
        const selectedDumpExp = matterScopedExpenses.filter(
          exp => selectedExpIds.has(getExpenseRowId(exp)) && isInBillingPeriod(exp)
        );
        const selectedDumpOB = matterScopedOtherBilling.filter(
          ob => selectedOBIds.has(ob.ob_id) && isInBillingPeriod(ob)
        );

        // On update, already-linked source rows are not in the unbilled lists —
        // merge them back in so "Add data dump" still produces detail pages.
        // Do not matter-scope linked rows: they stay on the invoice even if the
        // Project dropdown is changed during edit.
        const dataDumpItems = addDataDump ? {
          billableTimes: dedupeByKey(
            [...linkedDumpSources.billableTimes, ...selectedDumpBT],
            (bt) => String(bt.bt_id)
          )
            .filter((bt) => parseTimeToHours(bt.time_recorded) > 0)
            .map(bt => ({ ...bt, stored_amount: computeBilledTimeAmount(bt) })),
          fixedFees: dedupeByKey(
            [...linkedDumpSources.fixedFees, ...selectedDumpFF],
            (ff) => String(ff.assignedid)
          ),
          expenses: dedupeByKey(
            [...linkedDumpSources.expenses, ...selectedDumpExp],
            (exp) => String(getExpenseRowId(exp))
          ),
          otherBilling: dedupeByKey(
            [...linkedDumpSources.otherBilling, ...selectedDumpOB],
            (ob) => String(ob.ob_id)
          ),
        } : null;

        generateInvoicePDF(
          matterId,
          { from_name: fromName, address: fromAddress, phone: fromPhone, email: fromEmail },
          {
            id: clientInfo.id,
            name: clientInfo.name,
            email: clientInfo.email,
            phone: clientInfo.phone,
            address: clientInfo.address,
            company: clientInfo.company,
          },
          dueDate, matterName, displayAllLineItems, discountNum, taxRateNum,
          refNo, afeNumber, '', null,
          null, periodStr, terms, gstNumber, issueDate, dataDumpItems,
          blackAndWhitePdf, amountPaidNum, poNumber, lsdNumber, notesToClient
        );
      } catch (pdfErr) {
        console.error('PDF generation error:', pdfErr);
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving invoice:', error);
      const data = error?.data;
      const detail =
        (typeof data?.error === 'string' && data.error) ||
        (data?.errors && typeof data.errors === 'object'
          ? Object.entries(data.errors)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join('; ')
          : '') ||
        error?.message ||
        '';
      alert(`Failed to save invoice.${detail ? ' ' + detail : ''} Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  const fmt = (n: number) => n.toFixed(2);

  if (loading) {
    return (
      <div className="inv-overlay">
        <div className="inv-doc">
          <button className="inv-close-btn" onClick={onClose} aria-label="Close" title="Close">
            &times;
          </button>
          <div className="inv-loading"><div className="inv-spinner" /><p>Loading...</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="inv-overlay">
      <div className="inv-doc">
        <button className="inv-close-btn" onClick={onClose} aria-label="Close" title="Close">
          &times;
        </button>

        {/* ── Top Row: From + Invoice Title ── */}
        <div className="inv-top">
          <div className="inv-from">
            <textarea
              className="inv-input inv-from-name"
              aria-label="Sender name"
              value={fromName}
              onChange={e => setFromName(e.target.value)}
              placeholder="Your Company Name"
              rows={2}
            />
            <textarea
              className="inv-input inv-from-address"
              aria-label="Sender address"
              value={fromAddress}
              onChange={e => setFromAddress(e.target.value)}
              placeholder="Your Address"
              rows={2}
            />
            <input
              className="inv-input inv-from-email"
              aria-label="Sender email"
              value={fromEmail}
              onChange={e => setFromEmail(e.target.value)}
              placeholder="Email"
            />
            <input
              className="inv-input inv-from-phone"
              aria-label="Sender phone"
              value={fromPhone}
              onChange={e => setFromPhone(e.target.value)}
              placeholder="Phone"
            />
          </div>
          <div className="inv-title-block">
            <h1 className="inv-title">INVOICE</h1>
            <div className="inv-number-block">
              <div className="inv-number-row">
                <span className="inv-label">#</span>
                <input
                  className="inv-input inv-number-input"
                  name="sept-invoice-identifier"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  data-form-type="other"
                  value={invoiceNumber}
                  onChange={e => {
                    userEditedInvoiceNumber.current = true;
                    setInvoiceNumber(sanitizeSerial4Input(e.target.value));
                  }}
                  readOnly={readOnly}
                  placeholder="e.g. 482193"
                  aria-describedby={readOnly ? undefined : invoiceNumberHintId}
                />
              </div>
              {!readOnly && (
                <p className="inv-number-hint" id={invoiceNumberHintId}>
                  Numbers only. On create, this ID is saved as the invoice id if
                  it isnt already used; otherwise you will see an error. Auto-filled as last id + 1.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Bill To + Details ── */}
        <div className="inv-meta">
          <div className="inv-bill-to" role="group" aria-label="Bill to">
            <span className="inv-label">Bill To</span>
            {!clientInfo.id ? (
              <div className="inv-client-search-wrap">
                <input
                  className="inv-input"
                  value={clientSearch}
                  onChange={e => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Search for a client..."
                />
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="inv-client-dropdown">
                    {filteredClients.map((c: any) => {
                      const name = `${c.FirstName || c.first_name || ''} ${c.LastName || c.last_name || ''}`.trim() || 'Unnamed';
                      const company = c.Company || c.company || '';
                      return (
                        <div
                          key={c.ClientID || c.client_id}
                          className="inv-client-option"
                          onClick={() => handleClientSelect(c)}
                        >
                          <span className="inv-client-option-name">{name}</span>
                          {company && <span className="inv-client-option-company">{company}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                <input
                  className="inv-input"
                  value={clientInfo.address}
                  onChange={e => setClientInfo(p => ({ ...p, address: e.target.value }))}
                  placeholder="Address"
                />
                <div className="inv-client-selected-row">
                  <span className="inv-attention-label">Attention:</span>
                  <input
                    className="inv-input"
                    value={clientInfo.name}
                    onChange={e => setClientInfo(p => ({ ...p, name: e.target.value }))}
                    placeholder="Client Name"
                  />
                  <button className="inv-change-client-btn" onClick={() => {
                    setClientInfo({ id: '', name: '', email: '', phone: '', address: '', company: '' });
                    setClientMatters([]); setMatterId(''); setMatterName(''); setAfeNumber(''); setLsdNumber(''); setPoNumber('');
                    setBillableTimes([]); setFixedFees([]); setExpenses([]); setOtherBilling([]);
                    setLinkedDumpSources({ billableTimes: [], fixedFees: [], expenses: [], otherBilling: [] });
                    setSelectedBTIds(new Set()); setSelectedFFIds(new Set()); setSelectedExpIds(new Set()); setSelectedOBIds(new Set());
                  }}>Change</button>
                </div>
                {clientInfo.id ? (
                  <span className="inv-client-id">{clientInfo.id}</span>
                ) : null}
                <input
                  className="inv-input"
                  value={clientInfo.company}
                  onChange={e => setClientInfo(p => ({ ...p, company: e.target.value }))}
                  placeholder="Client Company"
                />
                <input
                  className="inv-input"
                  value={clientInfo.email}
                  onChange={e => setClientInfo(p => ({ ...p, email: e.target.value }))}
                  placeholder="Email"
                />
              </>
            )}
          </div>
          <div className="inv-details">
            {clientInfo.id && clientMatters.length > 0 && (
              <div className="inv-detail-row">
                <span className="inv-label">Project</span>
                {clientMatters.length === 1 ? (
                  <span className="inv-detail-value">{clientMatters[0].mattername || clientMatters[0].matterid}</span>
                ) : (
                  <select
                    className="inv-input inv-matter-select"
                    value={matterId}
                    onChange={e => {
                      const value = e.target.value;
                      const selected = clientMatters.find((m: any) => m.matterid === value);
                      setMatterId(value);
                      setMatterName(value ? (selected?.mattername || selected?.matterid || '') : 'All projects');
                      if (value) {
                        void syncMatterFieldsFromDetails(value);
                      } else {
                        setAfeNumber('');
                        setLsdNumber('');
                      }
                    }}
                  >
                    <option value="">All projects</option>
                    {clientMatters.map((m: any) => (
                      <option key={m.matterid} value={m.matterid}>
                        {m.mattername || m.matterid}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <div className="inv-detail-row">
              <span className="inv-label">AFE</span>
              <input className="inv-input" value={afeNumber} onChange={e => setAfeNumber(e.target.value)} placeholder="e.g. AFE-001" />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">LSD</span>
              <input className="inv-input" value={lsdNumber} onChange={e => setLsdNumber(e.target.value)} placeholder="e.g. LSD-001" />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">PO</span>
              <input className="inv-input" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-001" />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">Date</span>
              <input type="date" className="inv-input" aria-label="Invoice date" value={issueDate} onChange={e => setIssueDate(e.target.value)} />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">Due Date</span>
              <input type="date" className="inv-input" aria-label="Due date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div className="inv-detail-row">
              <span className="inv-label">Terms</span>
              <input className="inv-input" value={terms} onChange={e => setTerms(e.target.value)} placeholder="e.g. Net 30" />
            </div>
            <div className="inv-detail-row inv-balance-row">
              <span className="inv-label">Balance Due</span>
              <span className="inv-balance">${fmt(totals.balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* ── Billing Period ── */}
        {workspaceScope && <div className="inv-workspace-scope" role="status">
          <div><strong>From your uninvoiced workspace</strong><span>{workspaceScope.matterIds.length} selected project{workspaceScope.matterIds.length === 1 ? '' : 's'} · {workspaceScope.category ? CATEGORIES[workspaceScope.category] : 'All categories'}{workspaceScope.undatedOnly ? ' · Undated entries only' : workspaceScope.include_undated === false ? ' · Dated entries only' : ''}</span><span>Only matching work is available to import. Remove these limits to review all work.</span></div>
          <button type="button" onClick={() => { setWorkspaceScope(undefined); setBillingPeriodStart(''); setBillingPeriodEnd(''); }}>Remove limits</button>
        </div>}
        <div className="inv-billing-period">
          <span className="inv-label">Billing Period</span>
          <div className="inv-period-inputs">
            <input type="date" className="inv-input" aria-label="Billing period start" value={billingPeriodStart} onChange={e => { setBillingPeriodStart(e.target.value); setWorkspaceScope(scope => scope ? { ...scope, start_date: e.target.value } : scope); }} />
            <span className="inv-period-sep">to</span>
            <input type="date" className="inv-input" aria-label="Billing period end" value={billingPeriodEnd} onChange={e => { setBillingPeriodEnd(e.target.value); setWorkspaceScope(scope => scope ? { ...scope, end_date: e.target.value } : scope); }} />
          </div>
        </div>

        {/* ── Financials Loading ── */}
        {loadingFinancials && (
          <div className="inv-financials-loading">
            <div className="inv-spinner" />
            <span>Loading client financials...</span>
          </div>
        )}

        {/* ── Line Items Table ── */}
        <table className="inv-table">
          <thead>
            <tr>
              <th className="inv-th-name">Name</th>
              <th className="inv-th-code">Cost code</th>
              <th className="inv-th-hours">Hours</th>
              <th className="inv-th-rate">Rate</th>
              <th className="inv-th-amount">Amount ($)</th>
              <th className="inv-th-action"></th>
            </tr>
          </thead>
          <tbody>
            {displayImportedItems.map((item, i) => (
              <tr key={`imp-${i}`} className="inv-row">
                <td><span className="inv-cell-text">{item.description}</span></td>
                <td><span className="inv-cell-text">{item.item || '—'}</span></td>
                <td className="inv-cell-num">{item.qty}</td>
                <td className="inv-cell-num">${fmt(item.amount)}</td>
                <td className="inv-cell-num">${fmt(item.amount * item.qty)}</td>
                <td></td>
              </tr>
            ))}
            {editableManualIndices.map((i) => {
              const item = manualLineItems[i];
              return (
              <tr key={`man-${i}`} className="inv-row inv-row-editable">
                <td>
                  <input
                    className="inv-input inv-cell-input"
                    value={item.description}
                    onChange={e => updateManualLineItem(i, 'description', e.target.value)}
                    placeholder="Name"
                  />
                </td>
                <td>
                  <input
                    className="inv-input inv-cell-input"
                    value={item.item}
                    onChange={e => updateManualLineItem(i, 'item', e.target.value)}
                    placeholder="Cost code"
                  />
                </td>
                <td>
                  <input
                    className="inv-input inv-cell-input inv-cell-input-sm"
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={item.qty}
                    onChange={e => updateManualLineItem(i, 'qty', sanitizeDecimalInput(e.target.value))}
                  />
                </td>
                <td>
                  <input
                    className="inv-input inv-cell-input inv-cell-input-sm"
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={item.amount}
                    onChange={e => updateManualLineItem(i, 'amount', sanitizeDecimalInput(e.target.value))}
                  />
                </td>
                <td className="inv-cell-num">${fmt((parseFloat(item.amount) || 0) * (parseFloat(item.qty) || 1))}</td>
                <td>
                  <button className="inv-remove-btn" onClick={() => removeManualLineItem(i)} title="Remove">
                    &times;
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* ── Actions below table ── */}
        <div className="inv-table-actions">
          <button className="inv-add-line" onClick={addManualLineItem}>+ Line Item</button>
          {totalItemCount > 0 && (
            <button
              className="inv-import-btn"
              onClick={() => setShowImportPicker(!showImportPicker)}
            >
              {showImportPicker ? 'Hide' : 'Import'} Unbilled Items ({selectedImportCount}/{totalItemCount})
            </button>
          )}
        </div>

        {/* ── Import Picker ── */}
        {showImportPicker && totalItemCount > 0 && (
          <div className="inv-import-picker">
            {matterScopedBillableTimes.length > 0 && (
              <div className="inv-import-group" role="group" aria-label="Billable Time">
                <div className="inv-import-group-header">
                  <strong>Billable Time</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(matterScopedBillableTimes.map((i: any) => i.bt_id));
                      setSelectedBTIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {matterScopedBillableTimes.every((i: any) => selectedBTIds.has(i.bt_id)) && matterScopedBillableTimes.length > 0
                      ? 'Remove All'
                      : 'Add All'}
                  </button>
                </div>
                {matterScopedBillableTimes.map((item: any) => (
                  <label key={item.bt_id} className="inv-import-item">
                    <input type="checkbox" checked={selectedBTIds.has(item.bt_id)} onChange={() => toggleBT(item.bt_id)} />
                    <span className="inv-import-desc">{item.notes || `${item.employee_name || 'Unknown'} - ${item.time_recorded || ''}`}</span>
                    <span className="inv-import-amt">${fmt(computeBilledTimeAmount(item))}</span>
                  </label>
                ))}
              </div>
            )}
            {matterScopedFixedFees.length > 0 && (
              <div className="inv-import-group" role="group" aria-label="Fixed Fees">
                <div className="inv-import-group-header">
                  <strong>Fixed Fees</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(matterScopedFixedFees.map((i: any) => i.assignedid));
                      setSelectedFFIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {matterScopedFixedFees.every((i: any) => selectedFFIds.has(i.assignedid)) && matterScopedFixedFees.length > 0
                      ? 'Remove All'
                      : 'Add All'}
                  </button>
                </div>
                {matterScopedFixedFees.map((item: any) => (
                  <label key={item.assignedid} className="inv-import-item">
                    <input type="checkbox" checked={selectedFFIds.has(item.assignedid)} onChange={() => toggleFF(item.assignedid)} />
                    <span className="inv-import-desc">{item.fee_code} - {item.narrative}</span>
                    <span className="inv-import-amt">${fmt(parseFloat(item.fees || '0'))}</span>
                  </label>
                ))}
              </div>
            )}
            {matterScopedExpenses.length > 0 && (
              <div className="inv-import-group" role="group" aria-label="Expenses">
                <div className="inv-import-group-header">
                  <strong>Expenses</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(matterScopedExpenses.map((i: any) => getExpenseRowId(i)));
                      setSelectedExpIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {matterScopedExpenses.every((i: any) => selectedExpIds.has(getExpenseRowId(i))) && matterScopedExpenses.length > 0
                      ? 'Remove All'
                      : 'Add All'}
                  </button>
                </div>
                {matterScopedExpenses.map((item: any) => {
                  const id = getExpenseRowId(item);
                  return (
                    <label key={id} className="inv-import-item">
                      <input type="checkbox" checked={selectedExpIds.has(id)} onChange={() => toggleExp(id)} />
                      <span className="inv-import-desc">{item.description || 'Expense'}</span>
                      <span className="inv-import-amt">${fmt(typeof item.amount === 'number' ? item.amount : parseFloat(item.amount || item.fees || '0') || 0)}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {matterScopedOtherBilling.length > 0 && (
              <div className="inv-import-group" role="group" aria-label="Other Billing">
                <div className="inv-import-group-header">
                  <strong>Other Billing</strong>
                  <button
                    className="inv-import-toggle-all"
                    onClick={() => {
                      const allIds = new Set(matterScopedOtherBilling.map((i: any) => i.ob_id));
                      setSelectedOBIds(prev => prev.size === allIds.size ? new Set() : allIds);
                    }}
                  >
                    {matterScopedOtherBilling.every((i: any) => selectedOBIds.has(i.ob_id)) && matterScopedOtherBilling.length > 0
                      ? 'Remove All'
                      : 'Add All'}
                  </button>
                </div>
                {matterScopedOtherBilling.map((item: any) => (
                  <label key={item.ob_id} className="inv-import-item">
                    <input type="checkbox" checked={selectedOBIds.has(item.ob_id)} onChange={() => toggleOB(item.ob_id)} />
                    <span className="inv-import-desc">{item.description || 'Other Billing'}</span>
                    <span className="inv-import-amt">${fmt(parseFloat(item.amount || '0'))}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Totals ── */}
        <div className="inv-totals">
          <div className="inv-totals-left">
            <div className="inv-options">
              <label className="inv-option">
                <input type="checkbox" checked={blackAndWhitePdf} onChange={e => setBlackAndWhitePdf(e.target.checked)} />
                <span>Black & white PDF</span>
              </label>
              <label className="inv-option">
                <input type="checkbox" checked={addDataDump} onChange={e => setAddDataDump(e.target.checked)} />
                <span>Add data dump</span>
              </label>
            </div>
          </div>
          <div className="inv-totals-right">
            <div className="inv-total-line" role="group" aria-label="Subtotal">
              <span>Subtotal</span>
              <span>${fmt(totals.subtotal)}</span>
            </div>
            {discountNum > 0 && (
              <div className="inv-total-line">
                <span>Discount ({discountNum}%)</span>
                <span>-${fmt(totals.discountAmt)}</span>
              </div>
            )}
            <div className="inv-total-line inv-total-editable">
              <span>Discount</span>
              <div className="inv-total-input-wrap">
                <input
                  className="inv-input inv-total-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={discount}
                  aria-label="Discount percentage"
                  onChange={e => setDiscount(sanitizeDecimalInput(e.target.value))}
                />
                <span className="inv-pct">%</span>
              </div>
            </div>
            <div className="inv-total-line inv-total-editable">
              <span>Tax</span>
              <div className="inv-total-input-wrap">
                <input
                  className="inv-input inv-total-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={taxRate}
                  aria-label="Tax percentage"
                  onChange={e => {
                    userEditedTaxRate.current = true;
                    setTaxRate(sanitizeDecimalInput(e.target.value));
                  }}
                />
                <span className="inv-pct">%</span>
              </div>
            </div>
            {taxRateNum > 0 && (
              <div className="inv-total-line">
                <span>Tax ({taxRateNum}%)</span>
                <span>${fmt(totals.tax)}</span>
              </div>
            )}
            <div className="inv-total-line inv-total-grand" role="group" aria-label="Total">
              <span>Total</span>
              <span>${fmt(totals.total)}</span>
            </div>
            <div className="inv-total-line inv-total-editable">
              <span>Amount Paid</span>
              <div className="inv-total-input-wrap">
                <span className="inv-dollar">$</span>
                <input
                  className="inv-input inv-total-input inv-total-input-money"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amountPaid}
                  aria-label="Amount paid"
                  onChange={e => setAmountPaid(sanitizeDecimalInput(e.target.value))}
                />
              </div>
            </div>
            <div className="inv-total-line inv-total-balance">
              <span>Balance Due</span>
              <span>${fmt(totals.balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* ── Notes & GST ── */}
        <div className="inv-notes">
          <span className="inv-label">Notes</span>
          <textarea
            className="inv-input inv-notes-input"
            value={notesToClient}
            onChange={e => setNotesToClient(e.target.value)}
            readOnly={readOnly}
            maxLength={2000}
            aria-label="Notes to client"
            placeholder="Notes — any relevant information not already covered"
            rows={3}
          />
          <div className="inv-gst-row">
            <span className="inv-label">GST #</span>
            <input
              className="inv-input inv-gst-input"
              value={gstNumber}
              onChange={e => setGstNumber(e.target.value)}
              placeholder="e.g. 811818848RT0001"
            />
          </div>
        </div>

        {/* ── Footer Actions ── */}
        <div className="inv-footer">
          <button className="inv-btn-cancel" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button
              className="inv-btn-download"
              onClick={handleSubmit}
              disabled={
                isSaving
                || allLineItems.length === 0
                || Boolean(existingInvoice && addDataDump && loadingFinancials)
              }
            >
              {isSaving ? 'Saving...' : existingInvoice ? 'Update & Download' : 'Save & Download'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

/**
 * Do not instantiate the wizard until the tenant's runtime config has RESOLVED.
 *
 * The sender fields below are seeded by `useState` initialisers, which React runs once on the
 * first render — so gating inside the component would be too late, and a wizard opened before
 * `/api/config` lands would seed the product fallback (name, address, phone, GST) and keep it
 * even after the tenant's real config arrived. On a tax document that is the wrong company's
 * GST number. Blocking here is deliberate over prefilling-then-correcting: the fields are
 * editable and an operator who sees a plausible letterhead may not check it.
 *
 * 'unavailable' renders normally — a deployment with no tenant row is a legitimate steady
 * state, and the fallback is the right answer there.
 */
const InvoiceWizardGate: React.FC<InvoiceWizardProps> = (props) => {
  const status = useRuntimeConfigStatus();
  if (status === 'pending') {
    return (
      <div className="inv-overlay">
        <div className="inv-modal inv-modal-loading">
          <p>Loading organisation settings…</p>
        </div>
      </div>
    );
  }
  return <InvoiceWizard {...props} />;
};

export default InvoiceWizardGate;
